import { type numeric } from '@core/type/numeric'
import { IS_ON } from '@core/util/check.util'
import { SYMBOLS, SYMBOLS_BASE_256, SYMBOLS_LENGTH } from '@core/util/constant.util'
import { ENV_CSN_DEC_NUMBER, ENV_CSN_HEX_CARD, ENV_CSN_HEX_NUMBER } from '@core/util/env.util'
import { TO_STRING } from '@core/util/string.util'

export const TO_BOOLEAN = (v: any): boolean => IS_ON(v)
export const TO_NUMBER = (v: any): number => ((typeof v === 'boolean') ? (v ? 1 : 0) : Number.parseFloat(TO_STRING(v)))

/**
 * base convert big base small numbers
 */
export const BASE_CONVERT_BBSN = (n: numeric, from: numeric, to: numeric): string | null => {
  const N = `${n}`

  if ((+from <= 0) || (SYMBOLS_LENGTH < +from)) {
    console.log(`Base from ${from} not in 1..${SYMBOLS_LENGTH}`)
    return null
  }

  if ((+to <= 0) || (SYMBOLS_LENGTH < +to)) {
    console.log(`Base to ${to} not in 1..${SYMBOLS_LENGTH}`)
    return null
  }

  let nBaseTen = 0
  if (+from === 10) {
    nBaseTen = parseInt(N)
  } else {
    const sizeN = N.length
    for (let i = 0; i < sizeN; i += 1) {
      const Ni = N[i]

      let mul = 0
      let bMulOk = false
      while (mul < SYMBOLS_LENGTH) {
        bMulOk = Ni === SYMBOLS[mul]
        if (bMulOk) {
          break
        } else {
          mul += 1
        }
      }

      if (+from <= mul) {
        console.log(`Symbol not allowed in base ${from}`)
        return null
      }

      if (bMulOk) {
        console.log('Symbol not found')
        return null
      }

      const exp = sizeN - i - 1
      nBaseTen += mul * ((exp === 0) ? 1 : Math.pow(+from, exp))
    }
  }

  if (+to !== 10) {
    const nTo: any[] = (nBaseTen === 0) ? [SYMBOLS[0]] : []
    while (nBaseTen > 0) {
      const mod = nBaseTen % +to
      if ((mod < 0) || (SYMBOLS_LENGTH <= mod)) {
        console.log(`Out of bounds mod ${mod} not in 0..${SYMBOLS_LENGTH}`)
        return null
      }
      nTo.push(SYMBOLS[mod])
      nBaseTen = parseInt(`${nBaseTen / +to}`)
    }
    return nTo.reverse().toString().replace(/,/g, '')
  }

  return nBaseTen.toString()
}

/**
 * from pure numeric string in base [2,36] to base [2,36] else throws
 */
export const REBASE = (_: { from: numeric, base: number, toBase: number }): string => {
  const { from, base, toBase } = _

  const range = SYMBOLS.split('')
  const fromRange = range.slice(0, base)
  const toRange = range.slice(0, toBase)

  let decValue = `${from}`.split('').reverse().reduce(
    (carry, digit, index) => {
      const indexOfDigit = fromRange.indexOf(digit)
      if (indexOfDigit === -1) {
        throw new Error(`Invalid digit '${digit}' in '${from}' for base ${base}`)
      }
      carry += indexOfDigit * (Math.pow(base, index))
      return carry
    },
    0
  )

  let newValue = ''
  while (decValue > 0) {
    newValue = toRange[decValue % toBase] + newValue
    decValue = (decValue - (decValue % toBase)) / toBase
  }
  return (newValue === '') ? '0' : newValue
}

export const BIG_ADD_10 = (_x: numeric, _y: numeric): string => {
  let c = 0
  const r: number[] = []
  const x = `${_x}`.split('').map(Number)
  const y = `${_y}`.split('').map(Number)
  while ((x.length !== 0) || (y.length !== 0)) {
    const s = (x.pop() ?? 0) + (y.pop() ?? 0) + c
    r.unshift(s < 10 ? s : s - 10)
    c = s < 10 ? 0 : 1
  }
  if (c !== 0) {
    r.unshift(c)
  }
  return r.join('')
}

export const BIG_16_TO_BIG_10 = (_s: numeric): string => {
  let dec = '0'
  const input = (
    ((((typeof _s === 'number') && !isNaN(_s)) || ((typeof _s === 'string') && (_s !== 'NaN')))) ? `${_s}` : ''
  )
  const inputList = input.split('')
  let head: string | undefined = inputList[0]
  const tail = inputList.slice(1)
  while ((typeof head === 'string') && (head !== '')) {
    const n = parseInt(TO_STRING(head), 16)
    for (let t = 8; t !== 0; t >>= 1) {
      dec = BIG_ADD_10(dec, dec)
      if ((n & t) !== 0) {
        dec = BIG_ADD_10(dec, '1')
      }
    }
    head = tail.shift()
  }
  return (input === '') ? '' : dec
}

export const BIG_10_TO_BIG_16 = (_s: numeric): string => {
  const hex: Array<string | undefined> = []
  const sStr = (
    ((((typeof _s === 'number') && !isNaN(_s)) || ((typeof _s === 'string') && (_s !== 'NaN')))) ? `${_s}` : ''
  )
  if (sStr === '0') {
    hex.push('0')
  } else if ((typeof sStr === 'string') && (sStr !== '')) {
    const dec = sStr.split('')
    const sum: number[] = []
    let i: number
    let s: number
    while (dec.length !== 0) {
      s = +(dec.shift() ?? 0)
      for (i = 0; (s !== 0) || i < sum.length; i += 1) {
        s += ((sum[i] ?? 0)) * 10
        sum[i] = s % 16
        s = (s - sum[i]) / 16
      }
    }
    while (sum.length !== 0) {
      hex.push(sum.pop()?.toString(16))
    }
  }
  return hex.join('')
}

export const FROM_BASE_10_TO_16 = (_: numeric): string => BIG_10_TO_BIG_16(_)
export const FROM_BASE_16_TO_10 = (_: numeric): string => BIG_16_TO_BIG_10(_)

export const FROM_BASE_16_TO_CARD_SERIAL = (_: numeric): string => {
  const s = `${_}`.padStart(16, '0')
  const sLength = s.length
  return `${s.substring(sLength - 16, sLength - 8)} ${s.substring(sLength - 8, sLength)}`
}
export const FROM_BASE_10_TO_CARD_SERIAL = (_: numeric): string => FROM_BASE_16_TO_CARD_SERIAL(FROM_BASE_10_TO_16(_))

export const FROM_BASE_16_TO_CSN_DISPLAY = (_: numeric): string => {
  let res = TO_STRING(_)
  if (ENV_CSN_HEX_CARD) {
    res = FROM_BASE_16_TO_CARD_SERIAL(res)
  } else if (ENV_CSN_DEC_NUMBER) {
    res = FROM_BASE_16_TO_10(res)
  }
  return res
}
export const FROM_BASE_10_TO_CSN_DISPLAY = (_: numeric): string => {
  let res = TO_STRING(_)
  if (ENV_CSN_HEX_NUMBER || ENV_CSN_HEX_CARD) {
    res = FROM_BASE_10_TO_16(_)
    if (ENV_CSN_HEX_CARD) {
      res = FROM_BASE_16_TO_CARD_SERIAL(res)
    }
  }
  return res
}

export const OPTIONS_TO_KEY_VALUES = (opts: Array<{ value: any, label: any }>): any => opts.reduce(
  (kv: any, o) => ({ ...kv, [o.value]: o.label }),
  {}
)

export const TO_JSON = (v: any): string => {
  let res: string = ''
  if ((typeof v !== 'undefined') && (v !== null)) {
    try { res = JSON.stringify(v) } catch { }
  }
  return res
}

export const FROM_JSON = (json?: string): any => {
  let res: any
  if ((typeof json === 'string') && (json !== '')) {
    try { res = JSON.parse(json ?? '') } catch { }
  }
  return res
}

export const TRIM_DATA = (_: { object: any, depth?: number }): any => {
  const o: any = _.object
  const depth = _.depth ?? Number.MAX_SAFE_INTEGER

  let res: any
  if ((typeof o !== 'undefined') && (o !== null)) {
    if (Array.isArray(o)) {
      res = o.map((row: any) => TRIM_DATA({ object: row, depth: depth - 1 }))
    } else if (typeof o === 'object') {
      Object.keys(o as Record<string, any>).forEach(k => {
        if ((typeof o[k] !== 'undefined') && (o[k] !== null)) {
          if ((typeof res === 'undefined') || (res === null)) {
            res = {}
          }
          res[k] = o[k]
          if (Array.isArray(o[k])) {
            res[k] = o[k].map((row: any) => TRIM_DATA({ object: row, depth: depth - 1 }))
          } else if (typeof o[k] === 'object') {
            if (o[k] instanceof Date) {
              res[k] = o[k].toISOString()
            } else {
              res[k] = TRIM_DATA({ object: o[k], depth: depth - 1 })
            }
          }
        }
      })
    } else {
      res = o
    }
  }
  return res
}

export const BIT_SET = (_: { on: number, at: number, to: 0 | 1 }): number => {
  const mask = 1 << _.at
  return (_.on & ~mask) | ((_.to << _.at) & mask)
}

export const BIT_KILL = (_: { on: number, at: number }): number => {
  const mask = 1 << _.at
  return (_.on & ~mask) | ((0 << _.at) & mask)
}

// BIG base SMALL number input
export const PARSE_INT_FROM_CUSTOM_BASE = (_: { input: string, base: string }): number => _.base.indexOf(_.input)

export const SET_CHAR_AT = (
  _: { input: string, at: number, set: string }
): string => `${_.input.substring(0, _.at)}${_.set}${_.input.substring(_.at + _.set.length)}`

export const BYTE_ARRAY_LIKE_TO_BASE_256 = (o: any): string => {
  return [...(o ?? [])].map(v => BASE_CONVERT_BBSN(TO_STRING(v), 10, 256)).join('')
}

export const BASE_256_TO_ARRAY_10 = (s: string): number[] => {
  const base = SYMBOLS_BASE_256
  return [...(s ?? '')].map((input: string) => PARSE_INT_FROM_CUSTOM_BASE({ input, base }))
}

export const BASE_256_TO_ARRAY_16 = (s: string): string[] => BASE_256_TO_ARRAY_10(s).map(FROM_BASE_10_TO_16)

export const ARRAY_LIKE_10_TO_ARRAY_16 = (b: any): string[] => [...(b ?? [])].map(v => FROM_BASE_10_TO_16(TO_STRING(v)))

export const BUFFER_TO_UUID = (b: any): string => {
  const input = [...(b ?? [])].slice(0, 16)
  const parts = [
    input.slice(0, 4),
    input.slice(4, 6),
    input.slice(6, 8),
    input.slice(8, 10),
    input.slice(10, 16)
  ]
  return parts.map(p => ARRAY_LIKE_10_TO_ARRAY_16(p).map(s => s.padStart(2, '0')).join('')).join('-')
}

// export const convertUnsignedToSigned2bytes = ( v: number ): number => ( v > 32767 ) ? ( v - 65536 ) : v
// export const convertSignedToUnsigned2bytes = ( v: number ): number => ( v < 0 ) ? (v + 65536) : v

export const SIGNED_TO_UNSIGNED = (_: { value: number, bits: number }): number => {
  const maxUnsignedValue = Math.pow(2, _.bits)
  return (_.value < 0) ? (_.value + maxUnsignedValue) : _.value
}
export const UNSIGNED_TO_SIGNED = (_: { value: number, bits: number }): number => {
  const maxUnsignedValue = Math.pow(2, _.bits)
  return (_.value > ((maxUnsignedValue / 2) - 1)) ? (_.value - maxUnsignedValue) : _.value
}

// Buffer.from( 'test' ).toString( 'ascii' )
// export const BYTE_ARRAY_LIKE_TO_STRING = (b: any): string => [...(b ?? [])].map(v => String.fromCharCode(v)).join('')

export const IGNORE_DUPLICATES = (on: (element: any) => any): (elem: any) => boolean => {
  const already = new Set()
  return element => {
    const value = on(element)
    const bNew = !already.has(value)
    bNew && already.add(value)
    return bNew
  }
}

export const TO_UNIQUE_ARRAY = (_: { from: any[], on?: (element: any) => any }): any[] => {
  const { from, on } = _
  return ((typeof on !== 'undefined') && (on !== null)) ? from.filter(IGNORE_DUPLICATES(on ?? (e => true))) : Array.from(new Set(from))
}

export const PERIOD_EXPORT = (_: { min: any, max: any, label: string }): string => {
  return `${(_.min?.toISOString() ?? '') as string}_§§_${(_.max?.toISOString() ?? '') as string}_§§_${_.label}`
}
export const PERIOD_IMPORT = (_: { input: string, momentService: any }): { min: any, max: any, label: string } => {
  const [minIso, maxIso, label] = _.input.split('_§§_')
  return {
    min: _.momentService?.from({ input: minIso }) ?? minIso,
    max: _.momentService?.from({ input: maxIso }) ?? maxIso,
    label
  }
}

export function DISPLAY_NS (ns: any): string {
  const nsId = TO_STRING(ns?.nsId)
  return TO_STRING(ns?.nsName ?? ns?.nsNumber ?? ((nsId === '') ? '' : `ID ${nsId}`))
}

export function DISPLAY_NB (nb: any): string {
  const nbId = TO_STRING(nb?.nbId)
  return TO_STRING(nb?.nbIdentifier ?? nb?.nbNumber ?? ((nbId === '') ? '' : `ID ${nbId}`))
}

export function DISPLAY_NS_OR_NB (_: { ns: any, nb: any }): string {
  const nbD = DISPLAY_NB(_.nb)
  const nsD = DISPLAY_NS(_.ns)
  return (nbD !== '') ? nbD : nsD
}

export function TO_ANY_ARRAY (from: any): any[] {
  let res: any[] = []
  if (Array.isArray(from)) {
    res = from
  } else {
    try { res = Array.from(from as Iterable<any> | ArrayLike<any>) } catch { }
  }
  return res
}
